import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckSquare, FileText, Users, Mail, Plus, Archive, X, Circle, CheckCircle2, Trash2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Meeting {
  id: string;
  title: string;
  meeting_date: string;
  location: string | null;
  meeting_type: string | null;
  custom_meeting_type: string | null;
  topics: Topic[];
  invited_members: string[];
  status: string;
  archived_at: string | null;
  sections?: MeetingSection[];
}

interface Topic {
  title: string;
  details: string;
}

interface MeetingSection {
  id: string;
  title: string;
  content: string | null;
  section_order: number;
}

interface TopicMinutes {
  topicTitle: string;
  minutes: string;
}

interface AttendanceData {
  attended: string[];
  apologies: string[];
}

interface Member {
  id: string;
  full_name: string;
  email: string;
  user_roles: { role: string }[];
}

interface Task {
  id: string;
  title: string;
  description: string | null;
  task_type: 'personal' | 'assigned' | 'general';
  assigned_to: string | null;
  status: 'pending' | 'completed';
  created_at: string;
  assigned_members?: Member[];
}

function ArchivedMeetingsTable({ onViewMinutes, onRestore }: { onViewMinutes: (meeting: string) => void; onRestore: (meeting: string) => void }) {
  const [archivedMeetings, setArchivedMeetings] = useState<Meeting[]>([]);

  useEffect(() => {
    fetchArchivedMeetings();
  }, []);

  const fetchArchivedMeetings = async () => {
    const { data, error } = await (supabase as any)
      .from('meetings')
      .select('*')
      .not('archived_at', 'is', null)
      .order('meeting_date', { ascending: false });
    
    if (!error && data) setArchivedMeetings(data);
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Title</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {archivedMeetings.map((meeting) => (
          <TableRow key={meeting.id}>
            <TableCell className="font-medium">{meeting.title}</TableCell>
            <TableCell>{meeting.meeting_type}</TableCell>
            <TableCell>{new Date(meeting.meeting_date).toLocaleDateString()}</TableCell>
            <TableCell>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => onViewMinutes(`${meeting.title}`)}>
                  <FileText className="h-4 w-4 mr-1" />
                  Minutes
                </Button>
                <Button variant="outline" size="sm" onClick={() => onRestore(meeting.title)}>Restore</Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
        {archivedMeetings.length === 0 && (
          <TableRow>
            <TableCell colSpan={4} className="text-center text-muted-foreground">
              No archived meetings
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

export default function SecretaryDashboard() {
  const { toast } = useToast();
  const [isMeetingDialogOpen, setIsMeetingDialogOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  
  // Meeting form states
  const [meetingType, setMeetingType] = useState<string>('Committee');
  const [customMeetingType, setCustomMeetingType] = useState<string>('');
  const [topics, setTopics] = useState<Topic[]>([{ title: "", details: "" }]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [minutesText, setMinutesText] = useState<string>('');
  const [topicMinutes, setTopicMinutes] = useState<TopicMinutes[]>([]);
  const [attendees, setAttendees] = useState<string[]>([]);
  const [apologies, setApologies] = useState<string[]>([]);
  const [commRecipients, setCommRecipients] = useState<string[]>([]);
  
  // Task form states
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [taskType, setTaskType] = useState<'personal' | 'assigned' | 'general'>('personal');
  const [taskAssignees, setTaskAssignees] = useState<string[]>([]);

  useEffect(() => {
    fetchCurrentUser();
    fetchMeetings();
    fetchMembers();
    fetchTasks();
  }, []);

  const fetchCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUser(user);
  };

  const fetchMeetings = async () => {
    const { data, error } = await (supabase as any)
      .from('meetings')
      .select('*')
      .is('archived_at', null)
      .order('meeting_date', { ascending: false });
    
    if (!error && data) {
      // Update status based on date
      const updatedMeetings = data.map((meeting: Meeting) => {
        const meetingDate = new Date(meeting.meeting_date);
        const now = new Date();
        if (meetingDate < now && meeting.status === 'upcoming') {
          supabase.from('meetings').update({ status: 'taken-place' }).eq('id', meeting.id);
          return { ...meeting, status: 'taken-place' };
        }
        return meeting;
      });
      setMeetings(updatedMeetings);
    }
  };

  const fetchMembers = async () => {
    const { data, error } = await supabase
      .from('members')
      .select(`
        id,
        full_name,
        email,
        user_roles:user_roles(role)
      `)
      .eq('status', 'active')
      .order('full_name');
    
    if (!error && data) setMembers(data as any);
  };

  const fetchTasks = async () => {
    if (!currentUser) return;
    
    const { data, error } = await (supabase as any)
      .from('tasks')
      .select('*')
      .or(`task_type.eq.general,assigned_to.eq.${currentUser.id},created_by.eq.${currentUser.id}`)
      .order('created_at', { ascending: false });
    
    if (!error && data) setTasks(data);
  };

  const handleCreateMeeting = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    
    const { error } = await (supabase as any).from('meetings').insert({
      title: formData.get('title'),
      meeting_date: formData.get('meeting_date'),
      location: formData.get('location'),
      meeting_type: meetingType === 'Other' ? null : meetingType,
      custom_meeting_type: meetingType === 'Other' ? customMeetingType : null,
      topics: topics as any,
      invited_members: selectedMembers,
      status: 'upcoming',
      created_by: currentUser?.id
    });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    setIsMeetingDialogOpen(false);
    setTopics([{ title: "", details: "" }]);
    setSelectedMembers([]);
    setMeetingType('Committee');
    setCustomMeetingType('');
    fetchMeetings();
    
    toast({
      title: "Meeting created",
      description: "Meeting has been scheduled successfully.",
    });
  };

  const handleArchiveMeeting = async (meetingId: string) => {
    const meeting = meetings.find(m => m.id === meetingId);
    
    // Check if meeting has minutes
    const { data: sections } = await supabase
      .from('meeting_sections')
      .select('*')
      .eq('meeting_id', meetingId);
    
    if (!sections || sections.length === 0) {
      const confirmed = window.confirm(
        "This meeting has no minutes recorded. Are you sure you want to archive it?"
      );
      if (!confirmed) return;
    }

    const { error } = await supabase
      .from('meetings')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', meetingId);

    if (!error) {
      fetchMeetings();
      toast({
        title: "Meeting archived",
        description: "Meeting has been moved to archive.",
      });
    }
  };

  const handleSaveMinutes = async () => {
    if (!selectedMeeting) {
      toast({
        title: "Error",
        description: "No meeting selected.",
        variant: "destructive",
      });
      return;
    }

    // Build minutes content with topics and attendance
    let content = "ATTENDANCE\n\n";
    content += "Present:\n";
    attendees.forEach(id => {
      const member = members.find(m => m.id === id);
      if (member) content += `- ${member.full_name}\n`;
    });
    content += "\nApologies:\n";
    apologies.forEach(id => {
      const member = members.find(m => m.id === id);
      if (member) content += `- ${member.full_name}\n`;
    });
    content += "\n\nMINUTES\n\n";
    
    topicMinutes.forEach((tm, idx) => {
      content += `${idx + 1}. ${tm.topicTitle}\n${tm.minutes}\n\n`;
    });

    const { error } = await supabase
      .from('meeting_sections')
      .insert({
        meeting_id: selectedMeeting.id,
        title: 'Meeting Minutes',
        content: content,
        section_order: 0,
        created_by: currentUser?.id
      });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Minutes saved",
      description: "Meeting minutes have been saved successfully.",
    });
    
    setTopicMinutes([]);
    setAttendees([]);
    setApologies([]);
    setSelectedMeeting(null);
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newTaskTitle) {
      toast({
        title: "Error",
        description: "Please enter a task title.",
        variant: "destructive",
      });
      return;
    }

    // If assigned task, create task for each assignee
    if (taskType === 'assigned' && taskAssignees.length > 0) {
      for (const assigneeId of taskAssignees) {
        const { error } = await supabase.from('tasks').insert({
          title: newTaskTitle,
          description: newTaskDescription || null,
          task_type: taskType,
          assigned_to: assigneeId === 'me' ? currentUser?.id : assigneeId,
          status: 'pending',
          created_by: currentUser?.id
        });
        
        if (error) {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
          return;
        }
      }
    } else {
      // Personal or general task
      const { error } = await supabase.from('tasks').insert({
        title: newTaskTitle,
        description: newTaskDescription || null,
        task_type: taskType,
        assigned_to: taskType === 'personal' ? currentUser?.id : null,
        status: 'pending',
        created_by: currentUser?.id
      });

      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
        return;
      }
    }

    toast({
      title: "Task added",
      description: taskType === 'assigned' ? "Task has been assigned successfully." : "Task has been created.",
    });
    
    setNewTaskTitle("");
    setNewTaskDescription("");
    setTaskAssignees([]);
    fetchTasks();
  };

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    const { error } = await supabase
      .from('tasks')
      .update({ 
        status: newStatus,
        completed_at: newStatus === 'completed' ? new Date().toISOString() : null 
      })
      .eq('id', taskId);

    if (!error) {
      fetchTasks();
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId);

    if (!error) {
      fetchTasks();
    }
  };

  const handleViewMinutes = (meeting: string) => {
    toast({
      title: "View minutes",
      description: `${meeting} minutes would open here.`,
    });
  };
  
  const handleRestoreMeeting = (meeting: string) => {
    toast({
      title: "Meeting restored",
      description: `${meeting} has been restored from archive.`,
    });
  };
  
  const handleSendEmail = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "Email sent",
      description: "Your message has been sent to all recipients.",
    });
  };
  
  const handleSaveDraft = () => {
    toast({
      title: "Draft saved",
      description: "Your email draft has been saved.",
    });
  };

  const personalTasks = tasks.filter(t => t.task_type === 'personal' || t.task_type === 'assigned');
  const generalTasks = tasks.filter(t => t.task_type === 'general');

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            Secretary Dashboard
          </h1>
          <p className="text-muted-foreground mt-2">Manage meetings, minutes, and member coordination</p>
        </div>
      </div>

      <Tabs defaultValue="tasks" className="w-full">
        <TabsList className="inline-flex h-auto items-center justify-start gap-8 border-b border-border w-full">
          <TabsTrigger value="tasks" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Tasks</TabsTrigger>
          <TabsTrigger value="meetings" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Meetings</TabsTrigger>
          <TabsTrigger value="archive" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Archive</TabsTrigger>
          <TabsTrigger value="communications" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Communications</TabsTrigger>
        </TabsList>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle>Tasks</CardTitle>
              <CardDescription>Manage personal, assigned, and general tasks</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddTask} className="space-y-3 mb-6">
                <div className="flex gap-2">
                  <Input
                    placeholder="Task title..."
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    className="flex-1"
                    required
                  />
                  <Select value={taskType} onValueChange={(value: any) => setTaskType(value)}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personal">Personal</SelectItem>
                      <SelectItem value="assigned">Assigned</SelectItem>
                      <SelectItem value="general">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Textarea
                  placeholder="Task description (optional)..."
                  value={newTaskDescription}
                  onChange={(e) => setNewTaskDescription(e.target.value)}
                  rows={2}
                />
                {taskType === 'assigned' && (
                  <div className="space-y-2">
                    <Label>Assign to:</Label>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded p-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="assign-me"
                          checked={taskAssignees.includes('me')}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setTaskAssignees([...taskAssignees, 'me']);
                            } else {
                              setTaskAssignees(taskAssignees.filter(id => id !== 'me'));
                            }
                          }}
                        />
                        <Label htmlFor="assign-me" className="text-sm cursor-pointer font-medium">
                          ME
                        </Label>
                      </div>
                      {members.map((member) => (
                        <div key={member.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`task-member-${member.id}`}
                            checked={taskAssignees.includes(member.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setTaskAssignees([...taskAssignees, member.id]);
                              } else {
                                setTaskAssignees(taskAssignees.filter(id => id !== member.id));
                              }
                            }}
                          />
                          <Label htmlFor={`task-member-${member.id}`} className="text-sm cursor-pointer">
                            {member.full_name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <Button type="submit">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Task
                </Button>
              </form>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Personal & Assigned Tasks</h3>
                  <div className="space-y-2">
                    {personalTasks.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No personal tasks</p>
                    ) : (
                      personalTasks.map((task) => (
                        <div key={task.id} className="flex items-start gap-3 p-3 border rounded-lg">
                          <button
                            onClick={() => handleToggleTask(task.id, task.status)}
                            className="mt-0.5"
                          >
                            {task.status === 'completed' ? (
                              <CheckCircle2 className="h-5 w-5 text-green-600" />
                            ) : (
                              <Circle className="h-5 w-5 text-muted-foreground" />
                            )}
                          </button>
                          <div className="flex-1">
                            <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                              {task.title}
                            </p>
                            {task.description && (
                              <p className="text-sm text-muted-foreground">{task.description}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {task.task_type === 'personal' ? 'Personal' : 'Assigned'}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteTask(task.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">General Tasks</h3>
                  <div className="space-y-2">
                    {generalTasks.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No general tasks</p>
                    ) : (
                      generalTasks.map((task) => (
                        <div key={task.id} className="flex items-start gap-3 p-3 border rounded-lg">
                          <button
                            onClick={() => handleToggleTask(task.id, task.status)}
                            className="mt-0.5"
                          >
                            {task.status === 'completed' ? (
                              <CheckCircle2 className="h-5 w-5 text-green-600" />
                            ) : (
                              <Circle className="h-5 w-5 text-muted-foreground" />
                            )}
                          </button>
                          <div className="flex-1">
                            <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                              {task.title}
                            </p>
                            {task.description && (
                              <p className="text-sm text-muted-foreground">{task.description}</p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Meetings Tab */}
        <TabsContent value="meetings" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Meetings & Minutes</CardTitle>
                  <CardDescription>Manage meetings and record minutes</CardDescription>
                </div>
                <Dialog open={isMeetingDialogOpen} onOpenChange={setIsMeetingDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      New Meeting
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Meeting</DialogTitle>
                      <DialogDescription>Schedule a committee meeting</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateMeeting} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="meeting_title">Meeting Title</Label>
                        <Input id="meeting_title" name="title" required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="meeting_type">Meeting Type</Label>
                        <Select value={meetingType} onValueChange={setMeetingType} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select meeting type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AGM">AGM</SelectItem>
                            <SelectItem value="EGM">EGM</SelectItem>
                            <SelectItem value="Committee">Committee</SelectItem>
                            <SelectItem value="Event Planning">Event Planning</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {meetingType === 'Other' && (
                        <div className="space-y-2">
                          <Label htmlFor="custom_type">Custom Meeting Type</Label>
                          <Input
                            id="custom_type"
                            value={customMeetingType}
                            onChange={(e) => setCustomMeetingType(e.target.value)}
                            placeholder="Enter custom meeting type"
                            required
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label htmlFor="meeting_date">Meeting Date & Time</Label>
                        <Input id="meeting_date" name="meeting_date" type="datetime-local" required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="location">Location</Label>
                        <Input id="location" name="location" />
                      </div>
                      <div className="space-y-2">
                        <Label>Topics</Label>
                        {topics.map((topic, index) => (
                          <div key={index} className="space-y-2 p-3 border rounded">
                            <Input
                              placeholder="Topic title"
                              value={topic.title}
                              onChange={(e) => {
                                const newTopics = [...topics];
                                newTopics[index].title = e.target.value;
                                setTopics(newTopics);
                              }}
                            />
                            <Textarea
                              placeholder="Topic details"
                              value={topic.details}
                              onChange={(e) => {
                                const newTopics = [...topics];
                                newTopics[index].details = e.target.value;
                                setTopics(newTopics);
                              }}
                              rows={2}
                            />
                            {topics.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newTopics = topics.filter((_, i) => i !== index);
                                  setTopics(newTopics);
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setTopics([...topics, { title: "", details: "" }])}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Topic
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Label>Invite Members</Label>
                        <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded p-2">
                          {members.map((member) => (
                            <div key={member.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`member-${member.id}`}
                                checked={selectedMembers.includes(member.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedMembers([...selectedMembers, member.id]);
                                  } else {
                                    setSelectedMembers(selectedMembers.filter(id => id !== member.id));
                                  }
                                }}
                              />
                              <Label htmlFor={`member-${member.id}`} className="text-sm cursor-pointer">
                                {member.full_name}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <DialogClose asChild>
                          <Button type="button" variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button type="submit">Create Meeting</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {meetings.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No meetings scheduled</p>
                ) : (
                  meetings.map((meeting) => (
                    <div key={meeting.id} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold">{meeting.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            {meeting.meeting_type || meeting.custom_meeting_type}
                          </p>
                          <p className="text-sm">
                            {new Date(meeting.meeting_date).toLocaleString()}
                          </p>
                          {meeting.location && (
                            <p className="text-sm text-muted-foreground">{meeting.location}</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            Status: {meeting.status}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedMeeting(meeting);
                              // Initialize topic minutes from meeting topics
                              if (meeting.topics && Array.isArray(meeting.topics)) {
                                setTopicMinutes(meeting.topics.map((t: Topic) => ({ 
                                  topicTitle: t.title, 
                                  minutes: '' 
                                })));
                              }
                            }}
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Minutes
                          </Button>
                          {meeting.status === 'taken-place' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleArchiveMeeting(meeting.id)}
                            >
                              Archive
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Meeting Minutes Dialog */}
              <Dialog open={selectedMeeting !== null} onOpenChange={(open) => {
                if (!open) {
                  setSelectedMeeting(null);
                  setTopicMinutes([]);
                  setAttendees([]);
                  setApologies([]);
                }
              }}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{selectedMeeting?.title} - Minutes</DialogTitle>
                    <DialogDescription>Record meeting attendance and minutes for each topic</DialogDescription>
                  </DialogHeader>
                  {selectedMeeting && (
                    <div className="space-y-6">
                      <div>
                        <Label className="text-muted-foreground">Date & Time</Label>
                        <p>{new Date(selectedMeeting.meeting_date).toLocaleString()}</p>
                      </div>
                      
                      {/* Attendance */}
                      <div className="space-y-3 border-t pt-4">
                        <h3 className="font-semibold">Attendance</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label className="mb-2 block">Present</Label>
                            <div className="border rounded p-2 max-h-40 overflow-y-auto space-y-1">
                              {members.map((member) => (
                                <div key={member.id} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`attend-${member.id}`}
                                    checked={attendees.includes(member.id)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setAttendees([...attendees, member.id]);
                                        setApologies(apologies.filter(id => id !== member.id));
                                      } else {
                                        setAttendees(attendees.filter(id => id !== member.id));
                                      }
                                    }}
                                  />
                                  <Label htmlFor={`attend-${member.id}`} className="text-sm cursor-pointer">
                                    {member.full_name}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <Label className="mb-2 block">Apologies</Label>
                            <div className="border rounded p-2 max-h-40 overflow-y-auto space-y-1">
                              {members.map((member) => (
                                <div key={member.id} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`apology-${member.id}`}
                                    checked={apologies.includes(member.id)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setApologies([...apologies, member.id]);
                                        setAttendees(attendees.filter(id => id !== member.id));
                                      } else {
                                        setApologies(apologies.filter(id => id !== member.id));
                                      }
                                    }}
                                  />
                                  <Label htmlFor={`apology-${member.id}`} className="text-sm cursor-pointer">
                                    {member.full_name}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Topics and Minutes */}
                      <div className="space-y-3 border-t pt-4">
                        <h3 className="font-semibold">Topic Minutes</h3>
                        {topicMinutes.map((tm, index) => (
                          <div key={index} className="space-y-2 p-3 border rounded">
                            <Label className="font-medium">{index + 1}. {tm.topicTitle}</Label>
                            <Textarea
                              placeholder="Enter minutes for this topic..."
                              value={tm.minutes}
                              onChange={(e) => {
                                const updated = [...topicMinutes];
                                updated[index].minutes = e.target.value;
                                setTopicMinutes(updated);
                              }}
                              rows={3}
                            />
                          </div>
                        ))}
                        {topicMinutes.length === 0 && (
                          <p className="text-sm text-muted-foreground">No topics for this meeting</p>
                        )}
                      </div>

                      <div className="flex justify-end gap-2 border-t pt-4">
                        <Button variant="outline" onClick={() => {
                          setSelectedMeeting(null);
                          setTopicMinutes([]);
                          setAttendees([]);
                          setApologies([]);
                        }}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveMinutes}>
                          Save Minutes
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="archive" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Archived Meetings</CardTitle>
              <CardDescription>View and retrieve past meeting records</CardDescription>
            </CardHeader>
            <CardContent>
              <ArchivedMeetingsTable onViewMinutes={handleViewMinutes} onRestore={handleRestoreMeeting} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="communications" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Email Members</CardTitle>
              <CardDescription>Send communications to PTA members</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSendEmail} className="space-y-4">
                <div className="space-y-2">
                  <Label>Recipients</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded p-2">
                    {members.map((member) => (
                      <div key={member.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`email-member-${member.id}`}
                          checked={commRecipients.includes(member.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setCommRecipients([...commRecipients, member.id]);
                            } else {
                              setCommRecipients(commRecipients.filter(id => id !== member.id));
                            }
                          }}
                        />
                        <Label htmlFor={`email-member-${member.id}`} className="text-sm cursor-pointer">
                          {member.full_name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email-subject">Subject</Label>
                  <Input id="email-subject" placeholder="Email subject" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email-message">Message</Label>
                  <Textarea id="email-message" placeholder="Type your message here..." rows={8} required />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={handleSaveDraft}>
                    Save Draft
                  </Button>
                  <Button type="submit">
                    <Send className="h-4 w-4 mr-2" />
                    Send Email
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
